import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  isActive: boolean;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface FilterConfig {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  createdAfter?: string;
  createdBefore?: string;
  isActive?: boolean;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface OrganizationInfo {
  id: string;
  name: string;
  plan: string;
  seatLimit: number;
  activeUsersCount: number;
}

export interface UseUsersManagementReturn {
  // Data
  users: User[];
  totalCount: number;
  organizationInfo: OrganizationInfo | null;
  availableRoles: string[];
  
  // Loading states
  loading: boolean;
  loadingOrgInfo: boolean;
  
  // Error state
  error: string | null;
  
  // Pagination
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  
  // Filtering and Sorting
  filterConfig: FilterConfig;
  sortConfig: SortConfig;
  setFilterConfig: React.Dispatch<React.SetStateAction<FilterConfig>>;
  setSortConfig: React.Dispatch<React.SetStateAction<SortConfig>>;
  
  // Actions
  fetchUsers: () => Promise<void>;
  fetchOrganizationInfo: () => Promise<void>;
  handleFilterChange: (key: keyof FilterConfig, value: any) => void;
  clearFilters: () => void;
  handleSort: (key: string) => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useUsersManagement = (): UseUsersManagementReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({});
  const [availableRoles] = useState<string[]>(['ADMIN', 'MEMBER']);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
  const [loadingOrgInfo, setLoadingOrgInfo] = useState(true);

  const fetchOrganizationInfo = useCallback(async () => {
    try {
      setLoadingOrgInfo(true);
      // Auth handled via cookies, no need to check for token

      const response = await axios.get(`${API_URL}/api/organization`, {
        withCredentials: true
      });
      
      if (response.data.organization) {
        setOrganizationInfo(response.data.organization);
      }
    } catch (err: any) {
      logger.error('Error fetching organization info:', err);
      // We don't show this error directly to avoid cluttering the UI
    } finally {
      setLoadingOrgInfo(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      // Auth handled via cookies

      const response = await axios.get(`${API_URL}/api/users`, {
        withCredentials: true,
        params: {
          page: page + 1, // API expects 1-indexed pages
          limit: rowsPerPage,
          sortBy: sortConfig.key,
          sortOrder: sortConfig.direction,
          ...filterConfig
        }
      });
      
      const userData = response.data.users || [];
      setUsers(userData);
      
      // Make sure we always have a valid total count
      // If the backend doesn't return a total, use the number of users returned
      // but ensure we have at least as many total as we have users in the current page
      const total = response.data.pagination?.total;
      setTotalCount(total !== undefined ? total : Math.max(userData.length, totalCount));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterConfig, sortConfig, totalCount]);

  useEffect(() => {
    fetchUsers();
    fetchOrganizationInfo();
  }, [page, rowsPerPage, filterConfig, sortConfig]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (key: keyof FilterConfig, value: any) => {
    setFilterConfig(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
    setPage(0); // Reset to first page when filters change
  };
  
  const clearFilters = () => {
    setFilterConfig({});
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction if already sorting by this key
        return { 
          key, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // Default to descending when sorting by a new key
      return { key, direction: 'desc' };
    });
  };

  return {
    // Data
    users,
    totalCount,
    organizationInfo,
    availableRoles,
    
    // Loading states
    loading,
    loadingOrgInfo,
    
    // Error state
    error,
    
    // Pagination
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    
    // Filtering and Sorting
    filterConfig,
    sortConfig,
    setFilterConfig,
    setSortConfig,
    
    // Actions
    fetchUsers,
    fetchOrganizationInfo,
    handleFilterChange,
    clearFilters,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage
  };
};